import React, { useState } from 'react';
import { Box, Flex, HStack, Text, VStack, Icon } from '@chakra-ui/react';
import { FiHelpCircle, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { Collab, Campaign, isDeliveryOnlyUgc } from '@foodfluence/shared';
import { isClientHandledCredit } from '../../utils/collabUtils';
import { DEFAULT_MAX_PARTY_SIZE } from '../../scheduling/utils/partySizeUtils';
import { PRODUCT_SHIPPING_DAYS } from '../../../../constants/turnaroundConstants';
import { collabProductLabel } from '../../utils/collabProductLabel';
import {
  isBrandManagedCreditCompensation,
  isBrandManagedProductCompensation,
} from '../../../../utils/brandManagedCampaignUtils';
import { ff } from '../../../../theme';
import { MoneyFigure, Pill } from '../../../../components/ui';

interface CollabCompensationProps {
  collab: Collab;
  campaign: Campaign;
  isRequest?: boolean;
  instructionsOnly?: boolean;
}

interface FaqSectionProps {
  title: string;
  steps: string[];
  isOpen: boolean;
  onToggle: () => void;
  footer?: React.ReactNode;
}

const FaqSection: React.FC<FaqSectionProps> = ({ title, steps, isOpen, onToggle, footer }) => (
  <Box>
    <HStack
      as="button"
      w="100%"
      textAlign="left"
      minH="56px"
      py={3}
      spacing={3}
      align="center"
      cursor="pointer"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <Flex bg={ff.grey} boxSize="36px" borderRadius="full" color={ff.ink} flexShrink={0} align="center" justify="center">
        <Icon as={FiHelpCircle} boxSize="18px" />
      </Flex>
      <Text fontWeight={600} fontSize="15px" color={ff.ink} flex={1}>
        {title}
      </Text>
      <Icon as={isOpen ? FiChevronUp : FiChevronDown} boxSize={5} color={ff.muted} />
    </HStack>
    {isOpen && (
      <Box ml="48px" pl={3} mb={4} borderLeftWidth="2px" borderLeftColor={ff.line}>
        {footer ? (
          <Box>{footer}</Box>
        ) : (
          <VStack align="stretch" spacing={3}>
            {steps.map((step, i) => (
              <HStack key={i} spacing={3} align="flex-start">
                <Text fontSize="14px" color={ff.muted} fontWeight={600} minW={4} textAlign="right" flexShrink={0} lineHeight="1.5">
                  {i + 1}
                </Text>
                <Text fontSize="15px" color={ff.body} lineHeight="1.5">
                  {step}
                </Text>
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    )}
  </Box>
);

/** List row: title + supporting lines on the left, amount on the right. */
const DetailRow: React.FC<{ title: string; value?: string; children?: React.ReactNode }> = ({ title, value, children }) => (
  <HStack py={4} spacing={3} align="flex-start">
    <VStack align="start" spacing="4px" flex={1} minW={0}>
      <Text fontWeight={600} fontSize="15px" color={ff.ink}>
        {title}
      </Text>
      {children}
    </VStack>
    {value && (
      <Text fontWeight={600} fontSize="15px" color={ff.ink} flexShrink={0}>
        {value}
      </Text>
    )}
  </HStack>
);

const CollabCompensation: React.FC<CollabCompensationProps> = ({ collab, campaign, isRequest, instructionsOnly }) => {
  const [cashFaqOpen, setCashFaqOpen] = useState(false);
  const [creditFaqOpen, setCreditFaqOpen] = useState(false);
  const [productFaqOpen, setProductFaqOpen] = useState(false);

  const cashAmount = collab.cash ?? 0;
  const creditAmount = collab.credit ?? 0;
  const productValue = campaign?.productValue ?? 0;

  const isInStore = campaign?.campaignType === 'inStore';
  const isRetail = campaign?.campaignType === 'retail';
  const isShopify = campaign?.campaignType === 'shopify';
  const isShipToHome = campaign?.campaignType === 'shipToHome';
  const isBrandManagedProduct = isBrandManagedProductCompensation(campaign, collab);
  const isBrandManagedCredit = isBrandManagedCreditCompensation(campaign, collab);

  /** Direct Coordination credit uses the same UI as in-store credit. */
  const showInStoreStyleCredit = isInStore || isBrandManagedCredit;
  /** Direct Coordination product uses the same UI as ship-to-home / retail product gifts. */
  const showShippedProductStyle =
    isShopify || isShipToHome || isBrandManagedProduct;
  const showRetailProductStyle = isRetail && !isBrandManagedProduct;

  if (creditAmount <= 0 && cashAmount <= 0 && productValue <= 0) {
    return null;
  }

  const showCash = cashAmount > 0;
  const showCredit = creditAmount > 0;
  const showProduct = productValue > 0;
  const showDiscount = !!campaign?.shopifyAffiliateDiscountConfig;

  const productDescription = showShippedProductStyle
    ? 'Free product shipped directly to you'
    : showRetailProductStyle && collabProductLabel(collab, campaign)
      ? `Required items to purchase: ${collabProductLabel(collab, campaign)}`
      : showRetailProductStyle
        ? 'Free product'
        : 'Free product';

  const productNote = showShippedProductStyle
    ? `Product will be shipped within ${PRODUCT_SHIPPING_DAYS} days of collaboration acceptance`
    : null;

  const cashSteps = [
    'Complete your visit or content creation.',
    isDeliveryOnlyUgc(collab, campaign) ? 'Upload your content in the app.' : 'Submit your live post links through the app.',
    'Brand reviews and approves your content.',
    'Payment is released to your wallet within 3–5 business days.',
  ];

  const creditSteps = [
    'Schedule your visit',
    'Visit the location on your scheduled date',
    'When you\'re in store, click "Redeem Now" on your collab and present the approved credit amount to the staff',
    'The store will handle the rest!',
  ];

  const reimbursementSteps = [
    'Visit the store and purchase the listed product',
    'Keep your receipt - No receipt, no refund!',
    'Send the receipt to the brand in the inbox for reimbursement',
    'Brand will review and reimburse you',
  ];

  if (instructionsOnly) {
    const hasFaqs =
      showCash ||
      (showCredit && showInStoreStyleCredit) ||
      (showProduct && showRetailProductStyle);
    if (!hasFaqs) return null;
  }

  const compensationLabel = instructionsOnly ? 'Compensation instructions' : 'Compensation';

  return (
    <Box fontFamily="display">
      <Text as="h2" m={0} mb={3} textStyle="eyebrow" fontSize="12px" color={ff.text2}>
        {compensationLabel}
      </Text>
      {!instructionsOnly && (showCash || showCredit || showProduct) && (
        <VStack align="stretch" spacing="10px" bg={ff.limeSurface} borderRadius="20px" p="18px" mb={3}>
          <Text textStyle="eyebrow" fontSize="12px" color={ff.text2}>
            You get
          </Text>
          <Flex align="baseline" gap="18px" wrap="wrap">
            {showCash || showCredit ? (
              <>
                <MoneyFigure value={`$${cashAmount}`} label="Cash" size="29px" dim={!showCash} />
                <MoneyFigure value={`$${creditAmount}`} label="Credit" size="29px" dim={!showCredit} />
                {showProduct && <Pill tone="lime">{`+$${Math.round(campaign.productValue)} product`}</Pill>}
              </>
            ) : (
              <MoneyFigure value={`$${Math.round(campaign.productValue)}`} label="Product" size="29px" />
            )}
          </Flex>
        </VStack>
      )}
      <Box border="1px solid" borderColor={ff.card} borderRadius="20px" px={4}>
        <VStack spacing={0} align="stretch" divider={<Box h="1px" bg={ff.line} border="none" />}>
          {!instructionsOnly && showCash && (
            <DetailRow title="Cash" value={`$${collab.cash}`}>
              <Text fontSize="14px" color={ff.text2}>
                Paid on completion
              </Text>
            </DetailRow>
          )}

          {!instructionsOnly && showCredit && (
            <DetailRow title="Credit" value={`$${collab.credit}`}>
              {showInStoreStyleCredit && (
                <>
                  <Text fontSize="14px" color={ff.body} lineHeight="1.5">
                    {`Credit towards your experience for up to ${campaign?.maxPartySize ?? DEFAULT_MAX_PARTY_SIZE} people`}
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    Note: Gratuity/tip not included
                  </Text>
                </>
              )}
            </DetailRow>
          )}

          {!instructionsOnly && showProduct && (
            <DetailRow title="Free product gift" value={`$${Math.round(campaign.productValue)} value`}>
              <Text fontSize="14px" color={ff.body} lineHeight="1.5">
                {productDescription}
              </Text>
              {productNote && (
                <Text fontSize="13px" color={ff.text2}>
                  {productNote}
                </Text>
              )}
            </DetailRow>
          )}

          {!instructionsOnly && showDiscount && (
            <DetailRow title="Offered discount code">
              <Text fontSize="14px" color={ff.body}>
                {campaign.shopifyAffiliateDiscountConfig.discountType === 'percentage'
                  ? `${campaign.shopifyAffiliateDiscountConfig.discountValue}% off`
                  : `$${campaign.shopifyAffiliateDiscountConfig.discountValue} off`}
              </Text>
              {campaign.shopifyAffiliateDiscountConfig.minOrderAmount > 0 && (
                <Text fontSize="13px" color={ff.text2}>
                  Minimum order: ${campaign.shopifyAffiliateDiscountConfig.minOrderAmount}
                </Text>
              )}
              <Text fontSize="13px" color={ff.text2}>
                Code will be available after accepting. Share this discount code with your followers.
              </Text>
            </DetailRow>
          )}

          {showCash && (
            <FaqSection
              title="How do I get paid cash?"
              steps={cashSteps}
              isOpen={cashFaqOpen}
              onToggle={() => setCashFaqOpen(prev => !prev)}
            />
          )}

          {showCredit && showInStoreStyleCredit && (
            <FaqSection
              title="How do I redeem my credit?"
              steps={creditSteps}
              isOpen={creditFaqOpen}
              onToggle={() => setCreditFaqOpen(prev => !prev)}
              footer={isClientHandledCredit(collab, campaign) ? (
                <Box>
                  {/* Brand-handled credit has no fixed delivery method (on-site comp, gift card, reimbursement...),
                      so the brand's own instructions are the only description we show. */}
                  <Text fontSize="15px" color={ff.body} lineHeight="1.5">
                    The brand handles your credit directly.
                  </Text>
                  <Text fontSize="15px" color={ff.body} lineHeight="1.5" mt={1}>
                    {campaign?.creditInstructions || 'Reach out to the brand through your inbox for details.'}
                  </Text>
                </Box>
              ) : undefined}
            />
          )}

          {showProduct && showRetailProductStyle && (
            <FaqSection
              title="How do I get reimbursed?"
              steps={reimbursementSteps}
              isOpen={productFaqOpen}
              onToggle={() => setProductFaqOpen(prev => !prev)}
            />
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default CollabCompensation;
